import { Component } from "react"

/* Components */
import { InnerLayout } from "../../components"

/* REST API */
import { sessions } from "../../api/Player"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { SessionIcon, RoundIcon, TableIcon, ArrowIcon, AlertIcon, RefundIcon, ClockIcon, CopyIcon } from "./PlayerIcons"

/* Utils */
import { formatCount, formatSid, formatDay, formatTime, formatDuration } from "./PlayerUtils"

/* Sweet alert */
import Swal from "../../helpers/alert"

/* Translate */
import { t } from "../../translates/translate"

/* Redux */
import { connect } from "react-redux"


/* Widget Sessions */
class Sessions extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            sessions: [],
        }
    }

    componentDidMount = () => {
        this.load()
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { player } = this.props

        /* Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        sessions({ player }).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", sessions: response.data.sessions })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", sessions: [] })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", sessions: [] })
            }
            else {
                this.setState({ status: "error", sessions: [] })
            }
        }).catch(() => {
            this.setState({ status: "network", sessions: [] })
        })

    }

    /* Copy The Code Of A Session */
    copy = (value) => {

        /* Check Value */
        if (!value || !navigator.clipboard) {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(String(value)).then(() => {
            Swal.fire(utils.notification(t("plr.sidCopied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
        })

    }

    /* Copy The Code Of A Session — the click must not open the rounds of the session */
    copySid = (event, sid) => {

        event.stopPropagation()

        this.copy(sid)
    }

    /* Open The Rounds Of A Session */
    open = (item) => {

        /* Fields */
        const { openSession } = this.props

        /* Check Handler */
        if (openSession) {
            openSession(item.sid)
        }
    }

    render = () => {

        /* Fields */
        const { status, sessions: list } = this.state

        /* Check Status */
        if (status !== "ok") {
            return <InnerLayout inner status={status} reload={() => this.load(true)} />
        }

        /* Empty */
        if (!list || list.length === 0) {
            return (
                <div className="plr--empty inner">
                    <SessionIcon size={34} />
                    <span>{t("player.sessionsEmpty")}</span>
                </div>
            )
        }

        return (
            <div className="plr--sessions">

                {/* Hint */}
                <div className="plr--hint">
                    <SessionIcon size={15} />
                    <span>{t("plr.sessionsHint")}</span>
                </div>

                {/* Head */}
                <div className="plr--table-header inner">
                    <div className="plr--column plr--col-number">#</div>
                    <div className="plr--column plr--col-session">{t("player.sessionSid")}</div>
                    <div className="plr--column plr--col-game">{t("player.sessionLastGame")}</div>
                    <div className="plr--column plr--col-activity">{t("plr.activity")}</div>
                    <div className="plr--column plr--col-money">GGR</div>
                    <div className="plr--column plr--col-date">{t("player.sessionEntered")}</div>
                    <div className="plr--column plr--col-actions" />
                </div>

                {/* Body */}
                {list.map((item, index) => {

                    /* Fields */
                    const ggr = parseFloat(item.ggr) || 0

                    return (
                        <div onClick={() => this.open(item)} className="plr--row" key={`${index}`}>

                            {/* Number */}
                            <div className="plr--column plr--col-number">{index + 1}</div>

                            {/* Session */}
                            <div className="plr--column plr--col-session">

                                <div className="plr--session-icon">
                                    <SessionIcon size={16} />
                                </div>

                                <div className="plr--session-text">
                                    <div className="plr--session-code" title={utils.checkValue(item.sid, "")}>{formatSid(item.sid)}</div>
                                    <div className="plr--session-note">
                                        <ClockIcon size={11} />
                                        {formatDuration(item.enteredAt, item.lastTx)}
                                    </div>
                                </div>

                            </div>

                            {/* Game */}
                            <div className="plr--column plr--col-game">
                                <div className="plr--game-name">{utils.checkValue(item.lastGame, "—")}</div>
                                <div className="plr--game-note">
                                    <TableIcon size={11} />
                                    {formatCount(item.games)} {t("plr.gamesShort")}
                                </div>
                            </div>

                            {/* Activity */}
                            <div className="plr--column plr--col-activity">

                                <div className="plr--pill">
                                    <RoundIcon size={11} />
                                    <span>{formatCount(item.rounds)}</span>
                                </div>

                                <div className="plr--pill slate">
                                    <span>{formatCount(item.transactions)} tx</span>
                                </div>

                                {parseInt(item.failed) > 0 &&
                                    <div className="plr--pill red">
                                        <AlertIcon size={11} />
                                        <span>{formatCount(item.failed)}</span>
                                    </div>
                                }

                                {parseInt(item.refunds) > 0 &&
                                    <div className="plr--pill orange">
                                        <RefundIcon size={11} />
                                        <span>{formatCount(item.refunds)}</span>
                                    </div>
                                }

                            </div>

                            {/* Money */}
                            <div className="plr--column plr--col-money">

                                <div className={`plr--ggr ${ggr > 0 ? "green" : ""} ${ggr < 0 ? "red" : ""}`}>
                                    {ggr > 0 && "+ "}
                                    {ggr < 0 && "- "}
                                    {utils.convertor(Math.abs(ggr), "USD", 1)}
                                </div>

                                <div className="plr--money-row">
                                    <span className="plr--money debit">+ {utils.convertor(item.debit, "USD", 1)}</span>
                                    <span className="plr--money credit">- {utils.convertor(item.credit, "USD", 1)}</span>
                                </div>

                            </div>

                            {/* Date */}
                            <div className="plr--column plr--col-date">
                                <div className="plr--date">{formatDay(item.enteredAt)}</div>
                                <div className="plr--time">{formatTime(item.enteredAt)}</div>
                                <div className="plr--ago">{t("player.sessionLastTx")}: {formatTime(item.lastTx)}</div>
                            </div>

                            {/* Actions */}
                            <div className="plr--column plr--col-actions">

                                <div onClick={(event) => this.copySid(event, item.sid)} className="plr--action" title={t("plr.copySid")}>
                                    <CopyIcon size={15} />
                                </div>

                                <div className="plr--action blue" title={t("plr.openRounds")}>
                                    <ArrowIcon size={15} />
                                </div>

                            </div>

                        </div>
                    )
                })}

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps)(Sessions)
